import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { toast } from "react-toastify";

// Read from env, keep project URL private
const FUNCTIONS_BASE = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL;

// Permission groups
const PAGE_PERMS = [
  "page:Clockins",
  "page:Wastage",
  "page:Expenses",
  "page:ZReport",
  "page:Dashboard",
  "page:Debts",
  "page:Rota",
  "page:StaffRota",
];
const REPORT_PERMS = [
  "report:Wages",
  "report:ProfitLoss",
  "report:Wastage",
  "report:CashReceived",
  "report:CardTransactions",
  "report:SnappyDeliveries",
  "report:SupplierPayments",
  "report:Expenses",
  "report:Debts",
  "report:Stocktake",
  "report:MoneyFlow",
];
const ROLE_PRESETS = {
  admin: [...PAGE_PERMS, ...REPORT_PERMS],
  manager: [
    ...PAGE_PERMS,
    "report:Wages",
    "report:ProfitLoss",
    "report:Wastage",
    "report:Expenses",
    "report:MoneyFlow",
  ],
  staff: ["page:Clockins", "page:ZReport","page:Wastage", "page:Expenses", "page:Debts","page:Dashboard","page:MyRota","report:Wages","report:Debts"],
};

export default function AdminEmployees() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email: "",
    full_name: "",
    role: "staff",
    hourly_rate: 0,
    permissions: [...ROLE_PRESETS.staff],
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  async function fetchUsers() {
    setLoading(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) throw new Error("Not logged in");
      const token = session.access_token;

      const res = await fetch(`${FUNCTIONS_BASE}/users-admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || json.ok === false) throw new Error(json.message || "Failed to load users");
      setUsers(json.users || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  function onChange(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function togglePerm(perm) {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter((p) => p !== perm)
        : [...f.permissions, perm],
    }));
  }

  function applyRolePreset(role) {
    setForm((f) => ({ ...f, role, permissions: [...ROLE_PRESETS[role]] }));
  }

  async function saveEmployee() {
    if (!form.full_name || !form.role) {
      toast.error("Full name & role are required.");
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        const { error } = await supabase
          .from("employees")
          .update({
            full_name: form.full_name,
            role: form.role,
            hourly_rate: Number(form.hourly_rate) || 0,
            permissions: form.permissions || [],
          })
          .eq("id", editingId);

        if (error) throw error;
        setEditingId(null);
        resetForm();
        await fetchUsers();
        toast.success("Employee updated.");
      } else {
        if (!form.email) {
          toast.error("Email is required to invite a user.");
          return;
        }
        const res = await fetch(`${FUNCTIONS_BASE}/invite`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.ok === false) {
          if (json.code === "user_exists" && json.magicLink) {
            await navigator.clipboard.writeText(json.magicLink).catch(() => {});
            toast.info("User exists. Magic link copied to clipboard.");
            return;
          }
          throw new Error(json.message || "Invite failed");
        }
        toast.success("Invite sent!");
        resetForm();
        fetchUsers();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  function resetForm() {
    setForm({
      email: "",
      full_name: "",
      role: "staff",
      hourly_rate: 0,
      permissions: [...ROLE_PRESETS.staff],
    });
  }

  async function deleteUser(id) {
    if (!confirm("Delete this user from Auth and employees table?")) return;
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const token = session?.access_token;
      if (!token) throw new Error("Not logged in");

      const res = await fetch(`${FUNCTIONS_BASE}/users-admin?id=${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || json.ok === false) throw new Error(json.message || "Delete failed");

      toast.success("User deleted");
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function toggleLock(user) {
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const token = session?.access_token;
      const res = await fetch(`${FUNCTIONS_BASE}/lock-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ user_id: user.id, lock: !user.is_locked }),
      });
      const json = await res.json();
      if (!res.ok || json.ok === false) throw new Error(json.message || "Lock toggle failed");

      toast.success(user.is_locked ? "User unlocked" : "User locked & logged out");
      fetchUsers();
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function sendPasswordReset(user) {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: window.location.origin + "/update-password",
      });
      if (error) throw error;
      toast.success(`Password reset email sent to ${user.email}`);
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function reinviteUser(user) {
    try {
      const res = await fetch(`${FUNCTIONS_BASE}/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: user.email,
          role: user.role,
          full_name: user.full_name,
          hourly_rate: user.hourly_rate || 0,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.ok === false) {
        if (json.code === "user_exists" && json.magicLink) {
          await navigator.clipboard.writeText(json.magicLink).catch(() => {});
          toast.info("User exists. Magic link copied to clipboard.");
          return;
        }
        throw new Error(json.message || "Re-invite failed");
      }
      toast.success("Invite sent!");
    } catch (e) {
      toast.error(e.message);
    }
  }

  const card = { border: "1px solid #e5e7eb", borderRadius: 8, padding: 16, background: "#fff", marginBottom: 16 };
  const btn = { padding: "6px 10px", border: "none", borderRadius: 6, cursor: "pointer", background: "#2563eb", color: "#fff", fontWeight: 600 };
  const subtle = { ...btn, background: "#6b7280" };
  const danger = { ...btn, background: "#dc2626" };
  const warn = { ...btn, background: "#d97706" };

  return (
    <div style={{ maxWidth: 1180, margin: "24px auto", fontFamily: "Segoe UI, sans-serif" }}>
      <h2 style={{ marginBottom: 12 }}>🔧 Admin • Employees</h2>

      {/* FORM */}
      <div style={card}>
        <h3>{editingId ? "Update Employee" : "Invite / Create Employee"}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
          <div>
            <label>Email</label>
            <input style={{ width: "100%" }} value={form.email} onChange={(e) => onChange("email", e.target.value)} disabled={!!editingId} />
          </div>
          <div>
            <label>Full name</label>
            <input style={{ width: "100%" }} value={form.full_name} onChange={(e) => onChange("full_name", e.target.value)} />
          </div>
          <div>
            <label>Role</label>
            <select style={{ width: "100%" }} value={form.role} onChange={(e) => applyRolePreset(e.target.value)}>
              <option value="admin">admin</option>
              <option value="manager">manager</option>
              <option value="staff">staff</option>
            </select>
          </div>
          <div>
            <label>Hourly rate (£)</label>
            <input style={{ width: "100%" }} type="number" step="0.01" value={form.hourly_rate} onChange={(e) => onChange("hourly_rate", e.target.value)} />
          </div>
        </div>

        {/* Permissions */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 14 }}>
          <div>
            <strong>Pages</strong>
            {PAGE_PERMS.map((p) => (
              <label key={p} style={{ display: "block" }}>
                <input type="checkbox" checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} /> {p.replace("page:", "")}
              </label>
            ))}
          </div>
          <div>
            <strong>Reports</strong>
            {REPORT_PERMS.map((p) => (
              <label key={p} style={{ display: "block" }}>
                <input type="checkbox" checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} /> {p.replace("report:", "")}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
          <button style={btn} onClick={saveEmployee} disabled={saving}>
            {editingId ? "Save Changes" : "Invite / Create"}
          </button>
          <button style={subtle} onClick={() => { setEditingId(null); resetForm(); }}>Clear</button>
        </div>
      </div>

      {/* USERS TABLE */}
      <div style={card}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", minWidth: "900px", borderCollapse: "collapse" }}>
            <thead style={{ position: "sticky", top: 0, background: "#f3f4f6" }}>
              <tr>
                <th style={{ padding: 10 }}>Full Name</th>
                <th style={{ padding: 10 }}>Email</th>
                <th style={{ padding: 10 }}>Role</th>
                <th style={{ padding: 10 }}>Last Sign-in</th>
                <th style={{ padding: 10 }}>Locked</th>
                <th style={{ padding: 10, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} style={{ borderTop: "1px solid #e5e7eb", background: i % 2 ? "#fff" : "#fbfbfd", color: u.is_locked ? "#9ca3af" : "inherit" }}>
                  <td style={{ padding: 10 }}>{u.full_name || "—"}</td>
                  <td style={{ padding: 10 }}>{u.email}</td>
                  <td style={{ padding: 10 }}>{u.role || "—"}</td>
                  <td style={{ padding: 10 }}>
                    {u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString() : <em>Never signed in</em>}
                  </td>
                  <td style={{ padding: 10 }}>{u.is_locked ? "Yes" : "No"}</td>
                  <td style={{ padding: 10, display: "flex", gap: 6, justifyContent: "center" }}>
                    <button style={btn} onClick={() => reinviteUser(u)}>Re-invite</button>
                    <button style={warn} onClick={() => toggleLock(u)}>{u.is_locked ? "Unlock" : "Lock"}</button>
                    <button style={subtle} onClick={() => sendPasswordReset(u)}>Reset Password</button>
                    <button style={danger} onClick={() => deleteUser(u.id)}>Delete</button>
                  </td>
                </tr>
              ))}
              {!users.length && (
                <tr>
                  <td colSpan={6} style={{ padding: 16, textAlign: "center", color: "#6b7280" }}>No users yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
